import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { sitesAPI } from '../utils/api';
import { useSiteStore } from '../stores/siteStore';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import ConfirmationModal from '../components/ConfirmationModal';
import {
  PlusIcon,
  BuildingOffice2Icon,
  MapPinIcon,
  VideoCameraIcon,
  ShieldCheckIcon,
  XMarkIcon,
  PencilSquareIcon,
  TrashIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  CpuChipIcon,
  ViewColumnsIcon,
} from '@heroicons/react/24/outline';

const siteTypes = [
  { value: 'construction', label: 'Construction Site' },
  { value: 'manufacturing', label: 'Manufacturing Plant' },
  { value: 'warehouse', label: 'Warehouse' },
  { value: 'refinery', label: 'Refinery' },
  { value: 'mining', label: 'Mining Site' },
  { value: 'other', label: 'Other' },
];

export default function SitesPage() {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingSite, setEditingSite] = useState(null);
  const { setSelectedSite, selectedSite } = useSiteStore();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['sites'],
    queryFn: () => sitesAPI.getSites({ isActive: true }),
  });

  const deleteMutation = useMutation({
    mutationFn: (siteId) => sitesAPI.deleteSite(siteId),
    onSuccess: () => {
      toast.success('Site deleted successfully');
      queryClient.invalidateQueries({ queryKey: ['sites'] });
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to delete site');
    },
  });

  const sites = data?.data || [];

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDelete = (site) => {
    setDeleteTarget(site);
  };

  const handleSelectSite = (site) => {
    setSelectedSite(site);
    toast.success(`Switched to ${site.name}`);
  };

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Sites</h1>
          <p className="page-description">Manage your construction and industrial sites</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary flex items-center gap-2"
        >
          <PlusIcon className="w-5 h-5" />
          Add Site
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Total Sites"
          value={sites.length}
          icon={BuildingOffice2Icon}
          color="blue"
          delay={1}
        />
        <StatCard
          label="Active"
          value={sites.filter(s => s.status === 'active').length}
          icon={CheckCircleIcon}
          color="green"
          delay={2}
        />
        <StatCard
          label="Total Cameras"
          value={sites.reduce((sum, s) => sum + (s._count?.cameras || 0), 0)}
          // Note: Backend now returns only isActive: true cameras count
          icon={VideoCameraIcon}
          color="purple"
          delay={3}
        />
        <StatCard
          label="AI Devices"
          value={sites.reduce((sum, s) => sum + (s._count?.aiDevices || 0), 0)}
          icon={CpuChipIcon}
          color="orange"
          delay={4}
        />
      </div>

      {/* Sites Grid */}
      <div className="animate-slide-up stagger-2" style={{ opacity: 0, animationFillMode: 'forwards' }}>
        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-64 skeleton rounded-2xl"></div>
            ))}
          </div>
        ) : sites.length === 0 ? (
          <EmptyState onAdd={() => setShowAddModal(true)} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {sites.map((site, index) => (
              <SiteCard
                key={site.id}
                site={site}
                index={index}
                isSelected={selectedSite?.id === site.id}
                onSelect={() => handleSelectSite(site)}
                onEdit={() => setEditingSite(site)}
                onDelete={() => handleDelete(site)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {(showAddModal || editingSite) && (
        <SiteModal
          site={editingSite}
          onClose={() => {
            setShowAddModal(false);
            setEditingSite(null);
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          deleteMutation.mutate(deleteTarget.id);
          setDeleteTarget(null);
        }}
        title="Delete Site"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
      />
    </div>
  );
}

function StatCard({ label, value, icon: Icon, color, delay }) {
  const iconConfig = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-emerald-100 text-emerald-600',
    purple: 'bg-purple-100 text-purple-600',
    orange: 'bg-orange-100 text-orange-600',
  };

  return (
    <div className="stat-card">
      <div className="stat-card-header">
        <div className={clsx('w-10 h-10 rounded-xl flex items-center justify-center', iconConfig[color])}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
      <p className="stat-card-value">{value}</p>
      <p className="stat-card-label">{label}</p>
    </div>
  );
}

function SiteCard({ site, index, isSelected, onSelect, onEdit, onDelete }) {
  const statusConfig = {
    active: { badge: 'badge-success badge-dot', text: 'Active' },
    inactive: { badge: 'badge-neutral badge-dot', text: 'Inactive' },
    archived: { badge: 'badge-critical badge-dot', text: 'Archived' },
  };

  const status = statusConfig[site.status] || statusConfig.active;
  const compliance = site.metrics?.compliance || site.todayCompliance || 100;

  return (
    <div
      className={clsx(
        'glass-card p-5 transition-all duration-300 group cursor-pointer relative',
        isSelected && 'ring-2 ring-blue-500 ring-offset-2'
      )}
      onClick={onSelect}
    >
      {/* Selected indicator */}
      {isSelected && (
        <div className="absolute top-4 right-4">
          <CheckCircleIcon className="w-6 h-6 text-blue-500" />
        </div>
      )}

      {/* Header */}
      <div className="flex items-start gap-3 mb-4">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center shadow-lg flex-shrink-0">
          <BuildingOffice2Icon className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-slate-800 truncate">{site.name}</h3>
          <p className="text-sm text-slate-500">{site.code}</p>
        </div>
      </div>

      {/* Status & Location */}
      <div className="flex items-center gap-2 mb-4">
        <span className={clsx('badge badge-sm', status.badge)}>{status.text}</span>
        {site.city && (
          <span className="flex items-center gap-1 text-xs text-slate-500">
            <MapPinIcon className="w-3.5 h-3.5" />
            {site.city}
          </span>
        )}
      </div>

      {/* Type Badge */}
      <div className="mb-4">
        <span className="badge badge-neutral badge-sm capitalize">
          {site.type?.replace('_', ' ') || 'Construction'}
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-slate-50 rounded-lg p-2.5 text-center">
          <div className="flex items-center justify-center gap-1.5">
            <VideoCameraIcon className="w-4 h-4 text-blue-500" />
            {/* Display only active cameras count (backend filtered) */}
            <span className="font-bold text-slate-800">{site._count?.cameras || 0}</span>
          </div>
          <p className="text-[10px] text-slate-500 mt-0.5">Cameras</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-2.5 text-center">
          <div className="flex items-center justify-center gap-1.5">
            <ViewColumnsIcon className="w-4 h-4 text-purple-500" />
            <span className="font-bold text-slate-800">{site._count?.zones || 0}</span>
          </div>
          <p className="text-[10px] text-slate-500 mt-0.5">Zones</p>
        </div>
        <div className="bg-slate-50 rounded-lg p-2.5 text-center">
          <div className="flex items-center justify-center gap-1.5">
            <CpuChipIcon className="w-4 h-4 text-orange-500" />
            <span className="font-bold text-slate-800">{site._count?.aiDevices || 0}</span>
          </div>
          <p className="text-[10px] text-slate-500 mt-0.5">Devices</p>
        </div>
      </div>

      {/* Compliance */}
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm mb-1.5">
          <span className="text-slate-500 text-xs font-medium">Compliance</span>
          <span className={clsx(
            'font-bold text-sm',
            compliance >= 90 ? 'text-emerald-600' : compliance >= 70 ? 'text-amber-600' : 'text-red-600'
          )}>
            {compliance}%
          </span>
        </div>
        <div className="progress-bar">
          <div
            className={clsx(
              'progress-bar-fill',
              compliance >= 90 ? 'success' : compliance >= 70 ? 'warning' : 'danger'
            )}
            style={{ width: `${compliance}%` }}
          ></div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-all duration-200" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onEdit}
          className="flex-1 btn btn-secondary text-sm py-2 flex items-center justify-center gap-1.5"
        >
          <PencilSquareIcon className="w-4 h-4" />
          Edit
        </button>
        <button
          onClick={onDelete}
          className="icon-btn icon-btn-danger"
        >
          <TrashIcon className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function EmptyState({ onAdd }) {
  return (
    <div className="empty-state">
      <div className="empty-state-icon">
        <BuildingOffice2Icon className="w-10 h-10" />
      </div>
      <h3 className="empty-state-title">No sites yet</h3>
      <p className="empty-state-description">
        Create your first site to start monitoring safety and compliance across your operations.
      </p>
      <button onClick={onAdd} className="btn btn-primary">
        <PlusIcon className="w-5 h-5 mr-2" />
        Add Your First Site
      </button>
    </div>
  );
}

function SiteModal({ site, onClose }) {
  const queryClient = useQueryClient();
  const isEditing = !!site;

  const [formData, setFormData] = useState({
    name: site?.name || '',
    code: site?.code || '',
    type: site?.type || 'construction',
    address: site?.address || '',
    city: site?.city || '',
    state: site?.state || '',
    country: site?.country || '',
    capacity: site?.capacity || '',
  });

  const createMutation = useMutation({
    mutationFn: (data) => sitesAPI.createSite(data),
    onSuccess: () => {
      toast.success('Site created successfully');
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to create site');
    },
  });

  const updateMutation = useMutation({
    mutationFn: (data) => sitesAPI.updateSite(site.id, data),
    onSuccess: () => {
      toast.success('Site updated successfully');
      queryClient.invalidateQueries({ queryKey: ['sites'] });
      onClose();
    },
    onError: (error) => {
      toast.error(error.response?.data?.error || 'Failed to update site');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      capacity: formData.capacity ? parseInt(formData.capacity) : null,
    };

    if (isEditing) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  return (
    <div className="modal-backdrop">
      <div className="modal-container" onClick={onClose}>
        <div className="modal-content max-w-lg" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="modal-header">
            <h3 className="modal-title">
              {isEditing ? 'Edit Site' : 'Add New Site'}
            </h3>
            <button onClick={onClose} className="icon-btn">
              <XMarkIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="modal-body space-y-4">
          {/* Name & Code */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">
                Site Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input"
                placeholder="Main Construction Site"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">
                Site Code *
              </label>
              <input
                type="text"
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                className="input"
                placeholder="SITE-001"
                required
                disabled={isEditing}
              />
            </div>
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">
              Site Type
            </label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              className="input"
            >
              {siteTypes.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </select>
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">
              Address
            </label>
            <input
              type="text"
              value={formData.address}
              onChange={(e) => setFormData({ ...formData, address: e.target.value })}
              className="input"
              placeholder="123 Construction Ave"
            />
          </div>

          {/* City, State, Country */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">
                City
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="input"
                placeholder="Chennai"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">
                State
              </label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="input"
                placeholder="Tamil Nadu"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-2">
                Country
              </label>
              <input
                type="text"
                value={formData.country}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="input"
                placeholder="India"
              />
            </div>
          </div>

          {/* Capacity */}
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-2">
              Max Capacity (workers)
            </label>
            <input
              type="number"
              value={formData.capacity}
              onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
              className="input"
              placeholder="100"
              min="1"
            />
          </div>
            </div>

            {/* Actions */}
            <div className="modal-footer">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-secondary"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    {isEditing ? 'Updating...' : 'Creating...'}
                  </span>
                ) : (
                  isEditing ? 'Update Site' : 'Create Site'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
